import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { IndexedDB } from 'react-indexed-db-hook';
import { RequireAuth } from './components/Auth/RequireAuth/RequireAuth';
import { OwnerCompanySetupGate } from './components/Auth/OwnerCompanySetupGate';
import { Dashboard } from './pages/Dashboard';
import { ReportSent } from './pages/ReportSent';
import { NewCompanyRegistration } from './pages/NewCompanyRegistration';
import { Permission } from './utils/permissions';
import { AccountStatus } from './components/SubscriptionManagement';
import { ToDoPage } from './components/ToDoPage';
import { ReportLegacyRedirect } from './components/ReportLegacyRedirect';
import { EquipmentCategoryEditLegacyRedirect } from './components/pages-equipment';

/** Emails already sent use /pm?unitId=. Open that unit's PM Equipment Configuration. */
const PmEmailLinkRedirect = () => {
    const [searchParams] = useSearchParams();
    const unitId = searchParams.get('unitId')?.trim();
    const target = unitId
        ? `/pm-management/${encodeURIComponent(unitId)}`
        : '/pm-management';
    return <Navigate to={target} replace />;
};

export const AppRoutes = () => {
    return (
        <IndexedDB
            name="EquipmentInspection"
            version={5}
            objectStoresMeta={[
                {
                    store: 'media',
                    storeConfig: { keyPath: 'id', autoIncrement: true },
                    storeSchema: [
                        { name: 'name', keypath: 'name', options: { unique: false } },
                        { name: 'file', keypath: 'file', options: { unique: false } },
                        { name: 'question', keypath: 'question', options: { unique: false } },
                        { name: 'formId', keypath: 'formId', options: { unique: false } },
                    ],
                },
                {
                    store: 'signature',
                    storeConfig: { keyPath: 'id', autoIncrement: false },
                    storeSchema: [
                        { name: 'signatureData', keypath: 'signatureData', options: { unique: false } },
                        { name: 'equipmentId', keypath: 'equipmentId', options: { unique: false } },
                        { name: 'unitId', keypath: 'unitId', options: { unique: false } },
                        { name: 'equipmentName', keypath: 'equipmentName', options: { unique: false } },
                    ],
                },
            ]}
        >
            <OwnerCompanySetupGate>
            <Routes>
                <Route path="/" element={<Dashboard page="home" />} />
                <Route
                    path="/register/company"
                    element={
                        <RequireAuth>
                            <NewCompanyRegistration />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/personnel"
                    element={
                        <RequireAuth businessAdminOnly>
                            <Dashboard page="personnel" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/admin-training"
                    element={
                        <RequireAuth businessAdminOnly>
                            <Dashboard page="admin-training" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/resource-library"
                    element={
                        <RequireAuth businessAdminOnly>
                            <Dashboard page="site admin resources" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/site-admin"
                    element={
                        <RequireAuth businessAdminOnly>
                            <Dashboard page="site admin" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/customer/:companyId"
                    element={
                        <RequireAuth businessAdminOnly>
                            <Dashboard page="customer-profile" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/company/:companyId"
                    element={
                        <RequireAuth>
                            <Dashboard page="company-profile" />
                        </RequireAuth>
                    }
                />
                {/* Legacy email links sent company owners to /company/report/:id (invalid). Redirect to the real report route. */}
                <Route
                    path="/company/report/:id"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <ReportLegacyRedirect />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/company"
                    element={
                        <RequireAuth>
                            <Dashboard page="company-profile" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/my-profile"
                    element={
                        <RequireAuth>
                            <Dashboard page="my-profile" />
                        </RequireAuth>
                    }
                />
                <Route path="/skillset-setup" element={<Navigate to="/user-skillsets" replace />} />
                <Route
                    path="/user-skillsets"
                    element={
                        <RequireAuth>
                            <Dashboard page="user-skillsets" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/user-skillsets/:userId"
                    element={
                        <RequireAuth>
                            <Dashboard page="grade-user-skillsets" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <RequireAuth permission={Permission.VIEW_USERS}>
                            <Dashboard page="users" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/user/:userId"
                    element={
                        <RequireAuth>
                            <Dashboard page="user-profile" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/regions"
                    element={
                        <RequireAuth permission={Permission.MANAGE_REGIONS}>
                            <Dashboard page="regions" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/daily-briefing"
                    element={
                        <RequireAuth permission={Permission.MANAGE_SAFETY_AND_TRAINING}>
                            <Dashboard page="daily briefing" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/safety-training"
                    element={
                        <RequireAuth>
                            <Dashboard page="safety training" />
                        </RequireAuth>
                    }
                />
                <Route path="/schedule" element={<Navigate to="/safety-training" replace />} />
                <Route
                    path="/forms"
                    element={
                        <RequireAuth permission={Permission.MANAGE_FORMS}>
                            <Dashboard page="forms" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/forms/new"
                    element={
                        <RequireAuth permission={Permission.MANAGE_FORMS}>
                            <Dashboard page="new form" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/forms/edit/:formId"
                    element={
                        <RequireAuth permission={Permission.MANAGE_FORMS}>
                            <Dashboard page="edit form" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/forms/template/:templateId"
                    element={
                        <RequireAuth permission={Permission.MANAGE_FORMS}>
                            <Dashboard page="edit template" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/pm-forms"
                    element={
                        <RequireAuth permission={Permission.MANAGE_FORMS}>
                            <Dashboard page="pm forms" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/equipment"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <Dashboard page="equipment" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/equipment/category/new"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <Dashboard page="single unit" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/equipment/category/edit"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <EquipmentCategoryEditLegacyRedirect />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/equipment/category/edit/:categoryId"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <Dashboard page="single unit" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/equipment/new"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <Dashboard page="single unit" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/equipment/:unitId"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <Dashboard page="single unit" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/issues"
                    element={
                        <RequireAuth permission={Permission.VIEW_DISCREPANCIES}>
                            <Dashboard page="issues" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/issue/:id"
                    element={
                        <RequireAuth permission={Permission.VIEW_DISCREPANCIES}>
                            <Dashboard page="issue" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/issues/:issueId/edit"
                    element={
                        <RequireAuth permission={Permission.UPDATE_DISCREPANCIES}>
                            <Dashboard page="issue-edit" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/qr-codes"
                    element={
                        <RequireAuth permission={Permission.MANAGE_EQUIPMENT}>
                            <Dashboard page="qr-codes" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/reports/standard/:reportKey"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="standard-report" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/reports/custom/new"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="custom-report-new" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/reports/custom/:configId"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="custom-report-edit" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/reports/:unitId"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="reports" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/reports"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="reports" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/resources"
                    element={
                        <RequireAuth permission={Permission.VIEW_RESOURCES}>
                            <Dashboard page="resources" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/report/:source/:slug/:id"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="report" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/report/:id"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <ReportLegacyRedirect />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/lastreport/:id/:equipmentId"
                    element={
                        <RequireAuth permission={Permission.VIEW_REPORTS}>
                            <Dashboard page="report" noBreadCrumbs noNavBar />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/inspect/:id"
                    element={
                        <RequireAuth permission={Permission.INSPECT}>
                            <Dashboard page="inspect" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/report-sent"
                    element={
                        <RequireAuth permission={Permission.INSPECT}>
                            <ReportSent />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/safety-report/:id"
                    element={
                        <RequireAuth>
                            <Dashboard page="safety-report" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/pm"
                    element={
                        <RequireAuth permission={[Permission.VIEW_PM, Permission.MANAGE_PM]}>
                            <PmEmailLinkRedirect />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/pm-management"
                    element={
                        <RequireAuth permission={[Permission.VIEW_PM, Permission.MANAGE_PM]}>
                            <Dashboard page="pm management" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/pm-management/inspect/:unitId/:serviceLevelIds"
                    element={
                        <RequireAuth permission={[Permission.VIEW_PM, Permission.MANAGE_PM]}>
                            <Dashboard page="pm management" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/pm-management/:unitId"
                    element={
                        <RequireAuth permission={[Permission.VIEW_PM, Permission.MANAGE_PM]}>
                            <Dashboard page="pm management" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/cost-tracking"
                    element={
                        <RequireAuth permission={Permission.MANAGE_COST_TRACKING}>
                            <Dashboard page="cost tracking" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/tire-maintenance"
                    element={
                        <RequireAuth permission={Permission.MANAGE_TIRE_MAINTENANCE}>
                            <Dashboard page="tire maintenance" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/skill-tracking"
                    element={
                        <RequireAuth permission={Permission.MANAGE_SKILL_TRACKING}>
                            <ToDoPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/inbox"
                    element={
                        <RequireAuth>
                            <Dashboard page="inbox" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/help"
                    element={
                        <RequireAuth>
                            <Dashboard page="help" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <RequireAuth>
                            <Dashboard page="settings" />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/manage/subscription"
                    element={
                        <RequireAuth>
                            <Dashboard page="manage-subscription" />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/subscription"
                    element={
                        <RequireAuth>
                            <Dashboard page="manage-subscription" />
                        </RequireAuth>
                    }
                />
                <Route path="/account" element={<AccountStatus />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />
                {/* Free-demo confirm finishes while still on this URL; App shell mounts before Login navigates */}
                <Route path="/register/free-demo" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Dashboard page="not-found" />} />
            </Routes>
            </OwnerCompanySetupGate>
        </IndexedDB>
    );
};
